import { currencyFormatter, kwhPriceFormatter } from '@/helpers/currencyFormatter';
import { getViewUrl } from '@/helpers/documentHelpers';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { useGetAccountQuery } from '@/services/accounts';
import { useCreateChangeOrderMutation } from '@/services/changeOrders';
import type { CreateChangeOrderBody } from '@/services/changeOrders';
import { useGetCurrentContractQuery } from '@/services/contract';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetQuotesQuery } from '@/services/quotes';
import { PalmettoCheckbox } from '@palmetto/dsm-components';
import {
  Alert,
  Badge,
  Box,
  Button,
  Icon,
  RadioGroup,
  SelectInput,
  Spinner,
  TextInput,
  toast,
} from '@palmetto/palmetto-components';
import { Form, Formik, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  AdderType,
  AdditionalCostAdderType,
  BatteryPricingMethodOption,
  BatteryType,
  BatteryTypeLabels,
  CHANGE_ORDER_REASON_GROUP_LABELS,
  CHANGE_ORDER_REASON_GROUPS,
  CHANGE_ORDER_REASON_LABELS,
  ChangeOrderReasonCode,
  ContractDocumentProviders,
  ContractStatus,
  DocumentTypes,
  Quote,
  QuoteStatus,
  ViewUrlType,
} from 'types';

import { useIsOrgReadOnly } from '../auth/claimsUtils';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { Adders } from '../Quotes/Adders';
import { CreateQuoteFormikContext } from '../Quotes/CreateQuote';
import { useGetAccountPricing } from '../Quotes/hooks/useGetAccountPricing';
import { PricingTable } from '../Quotes/PricingTable';

interface CreateChangeOrderFormValues extends CreateQuoteFormikContext {
  systemSizeKw: string;
  systemFirstYearProductionKwh: string;
  reasons?: { label: string; value: ChangeOrderReasonCode }[];
  confirmedByEpc: boolean;
  // Battery-aware kwhRate of the selected product, synced from useGetAccountPricing(values) so
  // the submitted rate matches the pricing table rather than the no-battery parent fetch.
  kwhRate?: number;
}

const createChangeOrderSchema = yup.object().shape({
  productId: yup.string().required('Select a pricing option'),
  battery: yup.object().shape({
    type: yup
      .string()
      .when('$isBatterySelected', ([isBatterySelected], schema) =>
        isBatterySelected ? schema.oneOf(Object.values(BatteryType)) : schema.nullable(),
      ),
    pricingMethod: yup
      .string()
      .when('$isBatterySelected', ([isBatterySelected], schema) =>
        isBatterySelected ? schema.oneOf(Object.values(BatteryPricingMethodOption)) : schema.nullable(),
      ),
    manufacturer: yup.object().shape({
      label: yup.string(),
      value: yup
        .string()
        .when('$isBatterySelected', ([isBatterySelected], schema) =>
          isBatterySelected ? schema.required('Battery Manufacturer is required') : schema.nullable(),
        ),
    }),
    models: yup.array().when('$isBatterySelected', ([isBatterySelected], schema) =>
      isBatterySelected
        ? schema
            .of(
              yup.object().shape({
                modelNumber: yup.object().shape({
                  label: yup.string(),
                  value: yup.string().required('Battery Model Number is required'),
                }),
                quantity: yup.object().shape({
                  label: yup.string(),
                  value: yup.number().required('Battery Quantity is required'),
                }),
              }),
            )
            .min(1, 'At least one battery model is required')
        : schema
            .of(
              yup.object().shape({
                modelNumber: yup.object().shape({
                  label: yup.string(),
                  value: yup.string().nullable(),
                }),
                quantity: yup.object().shape({
                  label: yup.string(),
                  value: yup.number().nullable(),
                }),
              }),
            )
            .nullable(),
    ),
    cost: yup
      .number()
      .when('pricingMethod', {
        is: BatteryPricingMethodOption.rawCost,
        then: (schema) => schema.required('Battery cost is required'),
        otherwise: (schema) => schema.nullable(),
      })
      .min(0, 'Invalid battery cost: must be greater than 0'),
  }),
  // Not required and not user-editable in a change order: the cost is frozen (read-only, carried
  // from the current contract) because the change-order API can't amend it.
  solarReadinessCost: yup.number().nullable(),
  electricalUpgradeIncluded: yup.boolean(),
  // Optional (blank = unchanged); when provided, must be positive so a typo can't write a
  // nonsensical value to the system design. The inputs are string-typed, so map '' to undefined.
  systemSizeKw: yup
    .number()
    .transform((value, original) => (original === '' ? undefined : value))
    .moreThan(0, 'System size must be greater than 0')
    .nullable(),
  systemFirstYearProductionKwh: yup
    .number()
    .transform((value, original) => (original === '' ? undefined : value))
    .moreThan(0, 'Year-1 production must be greater than 0')
    .nullable(),
  reasons: yup
    .array()
    .min(1, 'At least one reason for the change order is required')
    .required('At least one reason for the change order is required'),
  confirmedByEpc: yup.boolean().oneOf([true], 'You must confirm before submitting'),
});

// Exhaustive so a new BatteryPricingMethodOption is a compile error rather than a blank card row.
const BATTERY_PRICING_METHOD_LABELS: Record<BatteryPricingMethodOption, string> = {
  [BatteryPricingMethodOption.embedded]: 'Included in system price',
  [BatteryPricingMethodOption.kwhRate]: '$/kWh',
  [BatteryPricingMethodOption.rawCost]: 'Total cost',
};

const REASON_OPTIONS = CHANGE_ORDER_REASON_GROUPS.map(({ group, reasons }) => ({
  label: CHANGE_ORDER_REASON_GROUP_LABELS[group],
  options: reasons.map((value) => ({ value, label: CHANGE_ORDER_REASON_LABELS[value] })),
}));

function ReasonSection() {
  const { values, setFieldValue, errors, touched } = useFormikContext<CreateChangeOrderFormValues>();
  const handleReasonsChange = useCallback(
    (event: { target: { value: { label: string; value: ChangeOrderReasonCode }[] } }) =>
      setFieldValue('reasons', event.target.value),
    [setFieldValue],
  );

  const reasonsError = touched.reasons ? errors.reasons : undefined;

  return (
    <FormSection title="Reason for Change Order">
      <Box maxWidth="3xl" width="100">
        <SelectInput
          id="reasons"
          name="reasons"
          label="Reason for Change Order *"
          placeholder="Select all that apply..."
          isMulti
          value={values.reasons}
          options={REASON_OPTIONS}
          onChange={handleReasonsChange}
          error={reasonsError}
        />
      </Box>
    </FormSection>
  );
}

function ConfirmationSection() {
  const { values, setFieldValue, errors, touched } = useFormikContext<CreateChangeOrderFormValues>();
  const handleConfirmChange = useCallback(
    (details: { checked: boolean | 'indeterminate' }) => {
      if (typeof details.checked === 'boolean') {
        setFieldValue('confirmedByEpc', details.checked);
      }
    },
    [setFieldValue],
  );

  return (
    // position: relative keeps the DSM checkbox's visually-hidden input anchored here; without a
    // positioned ancestor its offsetParent is <body>, so focusing it on click scrolls the page to top.
    <Box childGap="xs" padding={{ base: 'lg', desktop: 'lg 0' }} style={{ position: 'relative' }}>
      <PalmettoCheckbox
        id="confirmedByEpc"
        label="I understand this change order will be sent to the customer for acceptance and will amend the original PPA. The original contract will not be voided. EPC payouts can be affected."
        checked={values.confirmedByEpc}
        onCheckedChange={handleConfirmChange}
      />
      {touched.confirmedByEpc && errors.confirmedByEpc && (
        <Box fontSize="sm" fontColor="danger">
          {String(errors.confirmedByEpc)}
        </Box>
      )}
    </Box>
  );
}

function EscalationRateDefaulter() {
  const { values, setFieldValue } = useFormikContext<CreateChangeOrderFormValues>();
  const { escalationRates } = useGetAccountPricing(values);

  useEffect(() => {
    if (values.selectedEscalationRate === undefined && escalationRates?.length) {
      setFieldValue('selectedEscalationRate', escalationRates[0]);
    }
  }, [escalationRates, values.selectedEscalationRate, setFieldValue]);

  return null;
}

// The pricing-product fields this form reads. useGetAccountPricing returns `any[]`, so we narrow.
type SelectablePricingProduct = { productId: string; escalationRate?: number; kwhRate?: number };

// Syncs the selected product's kwhRate into the form. Pricing is fetched here with the live form
// values (including battery params), matching the products the PricingTable renders — the parent's
// no-arg useGetAccountPricing() omits battery params and would submit a non-battery-adjusted rate.
function SelectedProductPricingSync() {
  const { values, setFieldValue } = useFormikContext<CreateChangeOrderFormValues>();
  const { products } = useGetAccountPricing(values);

  const selectedKwhRate = useMemo(() => {
    // useGetAccountPricing returns loosely-typed products; narrow to the fields we read here.
    const selectedProduct = (products as SelectablePricingProduct[] | undefined)?.find(
      (p) => p.productId === values.productId && p.escalationRate === values.selectedEscalationRate,
    );
    return selectedProduct?.kwhRate;
  }, [products, values.productId, values.selectedEscalationRate]);

  useEffect(() => {
    if (values.kwhRate !== selectedKwhRate) {
      setFieldValue('kwhRate', selectedKwhRate);
    }
  }, [selectedKwhRate, values.kwhRate, setFieldValue]);

  return null;
}

function ChangeOrderFormActions({ isLoading, onCancel }: { isLoading: boolean; onCancel: () => void }) {
  const { isSubmitting, values } = useFormikContext<CreateChangeOrderFormValues>();
  const allowsAction = useIsOrgReadOnly();
  // kwhRate is included so the button stays disabled during the render gap between selecting
  // a product (sets productId synchronously) and SelectedProductPricingSync writing the rate.
  const canSubmit = Boolean(
    values.productId && values.kwhRate !== undefined && values.confirmedByEpc && values.reasons?.length,
  );
  return (
    <Box
      direction={{ base: 'column', tablet: 'row' }}
      alignItems={{ base: 'stretch', tablet: 'center' }}
      justifyContent={{ tablet: 'flex-end' }}
      childGap="sm"
      padding={{ base: 'lg', desktop: 'lg 0 0 0' }}
    >
      <Button
        type="button"
        variant="tertiary"
        tone="danger"
        size="md"
        onClick={onCancel}
        isDisabled={isLoading || isSubmitting}
      >
        Cancel
      </Button>
      <Button
        size="md"
        variant="primary"
        type="submit"
        isDisabled={isLoading || !canSubmit || !allowsAction}
        isLoading={isSubmitting}
      >
        Submit Change Order
      </Button>
    </Box>
  );
}

// Read-only "CURRENT CONTRACT" reference card shown beside the editable fields in each section.
function CurrentContractCard({
  rows,
  emptyMessage,
}: {
  rows: { value: React.ReactNode; label: string }[];
  emptyMessage?: string;
}) {
  return (
    <Box
      background="secondary"
      radius="md"
      padding="md"
      childGap="md"
      minWidth="3xl"
      height="fit-content"
      style={{ alignSelf: 'flex-start' }}
    >
      <Badge message="Current Contract" variant="info" style={{ width: 'fit-content' }} />
      {emptyMessage && rows.length === 0 ? (
        <Box fontSize="sm" color="body-secondary">
          {emptyMessage}
        </Box>
      ) : (
        rows.map((row) => (
          <Box key={row.label} childGap="2xs">
            <Box fontSize={{ base: 'md', desktop: 'lg' }} fontWeight="medium">
              {row.value}
            </Box>
            <Box fontSize="sm" color="body-secondary">
              {row.label}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

function SolarEnergySystemSection({
  currentContractRows,
}: {
  currentContractRows: { value: React.ReactNode; label: string }[];
}) {
  const { values, handleChange, errors, touched } = useFormikContext<CreateChangeOrderFormValues>();
  return (
    <FormSection title="Solar Energy System">
      <Box direction={{ base: 'column', desktop: 'row' }} gap="2xl" alignItems="flex-start">
        <Box flex="auto" childGap="lg" maxWidth="3xl" width="100">
          <TextInput
            id="systemSizeKw"
            name="systemSizeKw"
            type="number"
            label="New System Size (kW)"
            placeholder="e.g. 10"
            value={values.systemSizeKw}
            onChange={handleChange}
            error={touched.systemSizeKw && errors.systemSizeKw ? errors.systemSizeKw : undefined}
          />
          <TextInput
            id="systemFirstYearProductionKwh"
            name="systemFirstYearProductionKwh"
            type="number"
            label="New Year-1 Est. Production (kWh)"
            placeholder="e.g. 10"
            value={values.systemFirstYearProductionKwh}
            onChange={handleChange}
            error={
              touched.systemFirstYearProductionKwh && errors.systemFirstYearProductionKwh
                ? errors.systemFirstYearProductionKwh
                : undefined
            }
          />
        </Box>
        <CurrentContractCard rows={currentContractRows} />
      </Box>
    </FormSection>
  );
}

function AnnualEscalationRateSection({ currentEscalationLabel }: { currentEscalationLabel: string }) {
  const { values, setFieldValue } = useFormikContext<CreateChangeOrderFormValues>();
  const { escalationRates } = useGetAccountPricing(values);
  const options = useMemo(
    () =>
      (escalationRates || [])
        .map((rate) => ({ label: percentageFormatter.format(rate), value: rate.toString(), id: rate.toString() }))
        .toSorted((a, b) => Number(a.value) - Number(b.value)),
    [escalationRates],
  );
  return (
    <FormSection title="Annual Escalation Rate">
      <Box direction={{ base: 'column', desktop: 'row' }} gap="2xl" alignItems="flex-start">
        <Box flex="auto" childGap="sm" width="100">
          <Box fontSize="sm" fontWeight="medium">
            New Escalation Rate (%)
          </Box>
          {options.length ? (
            <RadioGroup
              name="escalationRate"
              value={values.selectedEscalationRate?.toString() || options[0]?.value}
              onChange={(event) => {
                setFieldValue('productId', undefined);
                setFieldValue('selectedEscalationRate', Number(event.target.value));
              }}
              options={options}
            />
          ) : null}
        </Box>
        <CurrentContractCard rows={[{ value: currentEscalationLabel, label: 'Escalation Rate' }]} />
      </Box>
    </FormSection>
  );
}

// Display-only recipients the change-order offer will be sent to. The backend derives these from
// the contract and generates the offer links server-side, so they are informational here.
function CustomerAlertDeliverySection({
  applicantEmail,
  coApplicantEmail,
}: {
  applicantEmail?: string;
  coApplicantEmail?: string;
}) {
  return (
    <FormSection title="Customer alert delivery">
      <Box childGap="sm">
        <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
          <Box flex="auto" childGap="2xs">
            <Box fontSize="sm" fontWeight="medium">
              Applicant Email
            </Box>
            <Box color="body-secondary">{applicantEmail || '—'}</Box>
          </Box>
          <Box flex="auto" childGap="2xs">
            <Box fontSize="sm" fontWeight="medium">
              Co-applicant Email
            </Box>
            <Box color="body-secondary">{coApplicantEmail || '—'}</Box>
          </Box>
        </Box>
        <Box fontSize="sm" color="body-secondary">
          Co-applicant, if any, will also receive their own personalized link via the same channels. Both must sign
          for the change order to take effect.
        </Box>
      </Box>
    </FormSection>
  );
}

// The current contract's pricing shown as a comparison row above the selectable pricing options,
// mirroring the table's columns.
function CurrentContractPricingRow({ quote }: { quote?: Quote }) {
  if (!quote) return null;
  const monthly =
    quote.systemPricingDetails?.[0]?.totalMonthlyPayment ?? quote.systemPricingDetails?.[0]?.monthlyPayment;
  const cols = [
    {
      header: 'Solar-Only Rate ($/kWh)',
      value: quote.kwhPrice != null ? kwhPriceFormatter.format(quote.kwhPrice) : '—',
    },
    {
      header: 'Estimated Solar-Only Year-1 Mo. Payment',
      value: monthly != null ? currencyFormatter.format(monthly) : '—',
    },
    {
      header: 'Solar-Only EPC Rate ($/W)',
      value: quote.pricePerWatt != null ? currencyFormatter.format(quote.pricePerWatt) : '—',
    },
    {
      header: 'Solar-Only EPC Total Payment',
      value: quote.totalSystemCost != null ? currencyFormatter.format(quote.totalSystemCost) : '—',
    },
  ];
  return (
    <Box radius="md" borderWidth="xs" borderColor="separator" background="secondary" padding="md" childGap="sm">
      <Badge message="Current Contract" variant="info" style={{ width: 'fit-content' }} />
      <Box direction={{ base: 'column', tablet: 'row' }} gap="lg">
        {cols.map((c) => (
          <Box key={c.header} flex="auto" childGap="2xs">
            <Box fontSize="xs" color="body-secondary" style={{ textTransform: 'uppercase' }}>
              {c.header}
            </Box>
            <Box fontWeight="medium">{c.value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// The signed contract this change order amends, with a "View PDF" button that opens the contract
// PDF in a new tab.
function OriginalContractReferenceSection({
  contractStatusLabel,
  isApproved,
  productName,
  recipient,
  contractPdfUrl,
}: {
  contractStatusLabel: string;
  isApproved: boolean;
  productName?: string;
  recipient?: { name?: string; email?: string };
  contractPdfUrl?: string;
}) {
  return (
    <FormSection title="Original Contract Reference">
      <Box childGap="md">
        <Box radius="md" borderWidth="xs" borderColor="separator" padding="md" childGap="md">
          <Box
            direction={{ base: 'column', tablet: 'row' }}
            justifyContent="space-between"
            alignItems={{ tablet: 'center' }}
            gap="md"
          >
            <Box childGap="xs">
              <Badge
                message={contractStatusLabel}
                variant={isApproved ? 'success' : 'info'}
                style={{ width: 'fit-content' }}
              />
              <Box>{productName || '—'}</Box>
              {recipient?.email && (
                <Box color="body-secondary" fontSize="sm">
                  {recipient.name} - sent to {recipient.email}
                </Box>
              )}
            </Box>
            <Button
              as="a"
              href={contractPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              tone="neutral"
              size="sm"
              isDisabled={!contractPdfUrl}
            >
              View PDF
            </Button>
          </Box>
        </Box>
        <Alert
          hasIcon
          variant="info"
          message="NTP status and all installer workflow milestones are fully preserved and unaffected by this change order."
        />
      </Box>
    </FormSection>
  );
}

export const CreateChangeOrder = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Seeded from the "Create Change Order" CTA on the Production and System Size Change Order Check
  // screen, which passes the as-built size the EPC just entered. Only used when it parses as a
  // positive finite number; otherwise the field stays blank like today.
  const systemSizeKwParam = searchParams.get('systemSizeKw');
  const parsedSystemSizeKwParam = systemSizeKwParam !== null ? Number(systemSizeKwParam) : NaN;
  const initialSystemSizeKw =
    Number.isFinite(parsedSystemSizeKwParam) && parsedSystemSizeKwParam > 0 ? systemSizeKwParam! : '';

  // Same CTA also passes the as-built year-1 production (e.g. from the out-of-tolerance production
  // check), so the form opens with both changed values already set. Same positive-finite guard.
  const productionParam = searchParams.get('systemFirstYearProductionKwh');
  const parsedProductionParam = productionParam !== null ? Number(productionParam) : NaN;
  const initialProductionKwh =
    Number.isFinite(parsedProductionParam) && parsedProductionParam > 0 ? productionParam! : '';

  const { data: accountData, isLoading: accountIsLoading } = useGetAccountQuery(id!);
  const { data: currentContract, isLoading: contractIsLoading } = useGetCurrentContractQuery(id!, { skip: !id });
  const { data: quotes, isLoading: quotesIsLoading } = useGetQuotesQuery(
    { id: id!, programType: accountData?.programType },
    { skip: !accountData?.programType },
  );
  const [createChangeOrder] = useCreateChangeOrderMutation();

  // Only the account/contract/quotes queries feed initialValues and the eligibility gate. Pricing is
  // fetched by the child sections with live form values (battery params) and shows its own loading in
  // the PricingTable, so there's no need for a separate no-args useGetAccountPricing() here.
  const isLoading = accountIsLoading || contractIsLoading || quotesIsLoading;

  const hasEligibleContract =
    currentContract?.status === ContractStatus.approved || currentContract?.status === ContractStatus.signed;

  const currentQuote = quotes?.find(
    (q: Quote) => q.status === QuoteStatus.active || q.status === QuoteStatus.contracted,
  ) as Quote | undefined;

  // Seed the battery editor from the current contract's battery so a contracted battery shows
  // selected rather than blank. The stored batteryDetails carries manufacturer/model as plain
  // strings and the cost as a separate additionalCostAdders entry; the editor's SelectInputs want
  // { label, value } objects and a nested cost, so map into that shape here.
  const initialBattery = useMemo(() => {
    const batteryDetails = currentQuote?.batteryDetails;
    if (!batteryDetails?.type) return undefined;
    const cost = currentQuote?.additionalCostAdders?.find(
      (a) => a.type === AdditionalCostAdderType.backupBattery || a.type === AdditionalCostAdderType.arbitrageBattery,
    )?.cost;
    return {
      type: batteryDetails.type,
      // A contracted/active battery always carries a pricing method.
      pricingMethod: batteryDetails.pricingMethod!,
      manufacturer: { label: batteryDetails.manufacturer ?? '', value: batteryDetails.manufacturer ?? '' },
      models: (batteryDetails.models ?? []).map((model) => ({
        modelNumber: { label: model.modelNumber, value: model.modelNumber },
        quantity: { label: String(model.quantity), value: model.quantity },
      })),
      ...(cost !== undefined && { cost }),
    };
  }, [currentQuote]);

  // The card is the pre-change-order baseline, so every value here comes off currentQuote only and is
  // never re-derived from form state. Electrical upgrade shows no dollar amount because the quote does
  // not store one — the figure beside the checkbox is live price-sheet config, not contract history.
  const currentAdderRows = useMemo(() => {
    if (!currentQuote) return [];

    const electricalUpgradeIncluded = currentQuote.adders?.includes(AdderType.electricalUpgrade) ?? false;
    const solarReadinessIncluded = currentQuote.adders?.includes(AdderType.solarReadiness) ?? false;
    const battery = currentQuote.batteryDetails?.type ? currentQuote.batteryDetails : undefined;

    if (!electricalUpgradeIncluded && !solarReadinessIncluded && !battery) return [];

    const rows: { value: React.ReactNode; label: string }[] = [
      { value: electricalUpgradeIncluded ? 'Included' : 'Not included', label: 'Electrical Upgrade' },
    ];

    const solarReadinessCost = currentQuote.additionalCostAdders?.find(
      (a) => a.type === AdditionalCostAdderType.solarReadiness,
    )?.cost;
    rows.push({
      value: solarReadinessIncluded
        ? solarReadinessCost !== undefined
          ? currencyFormatter.format(solarReadinessCost)
          : 'Included'
        : 'Not included',
      label: 'Solar Readiness',
    });

    if (battery) {
      rows.push({ value: BatteryTypeLabels[battery.type] ?? 'Battery', label: 'Battery' });
      rows.push({ value: battery.manufacturer || '—', label: 'Manufacturer' });
      if (battery.models?.length) {
        rows.push({
          value: battery.models.map((m) => `${m.modelNumber} (×${m.quantity})`).join(', '),
          label: 'Model',
        });
      }
      if (battery.pricingMethod) {
        rows.push({ value: BATTERY_PRICING_METHOD_LABELS[battery.pricingMethod], label: 'Pricing Method' });
      }
      // A kwhRate battery has no stored cost (it is priced into the system PPW), so the row is omitted
      // rather than showing $0.
      const batteryCost = currentQuote.additionalCostAdders?.find(
        (a) =>
          a.type === AdditionalCostAdderType.backupBattery || a.type === AdditionalCostAdderType.arbitrageBattery,
      )?.cost;
      if (batteryCost !== undefined) {
        rows.push({ value: currencyFormatter.format(batteryCost), label: 'Battery Cost' });
      }
    }

    return rows;
  }, [currentQuote]);

  // Offer-link recipients are derived from the signed DocuSign contract (applicant first,
  // co-applicant second). Shown read-only under "Customer alert delivery".
  const recipients =
    currentContract?.contractDocuments?.find((cd) => cd.provider === ContractDocumentProviders.docusign)
      ?.recipients ?? [];
  const contractStatusLabel = currentContract?.status === ContractStatus.approved ? 'Approved' : 'Signed';
  // Prefer the account's friendly name; fall back to the primary contract signer when it's absent.
  const customerName = accountData?.friendlyName || recipients[0]?.name;

  // The contract PDF is stored as a `contract`-type account document; link "View PDF" to it.
  const { data: contractDocuments } = useGetDocumentsQuery(
    { accountId: id!, documentTypeFilter: [DocumentTypes.contract] },
    { skip: !id },
  );
  const contractPdfUrl = getViewUrl(contractDocuments?.[0]?.files?.[0]?.viewUrls, ViewUrlType.original);

  const handleSubmit = async (
    values: CreateChangeOrderFormValues,
    { setSubmitting }: { setSubmitting: (s: boolean) => void },
  ) => {
    setSubmitting(true);

    if (
      values.kwhRate === undefined ||
      values.selectedEscalationRate === undefined ||
      values.selectedEscalationRate === null
    ) {
      toast.error('Please select a pricing option and escalation rate');
      setSubmitting(false);
      return;
    }

    if (!values.reasons?.length) {
      toast.error('Please select at least one reason for this change order');
      setSubmitting(false);
      return;
    }

    // Flat, full desired-state body — send every field. The core scalars baseline off the current
    // active quote (an executed change order swaps the active quote's size/production but leaves
    // account.systemDesign untouched, so systemDesign would be pre-change-order values).
    const body: CreateChangeOrderBody = {
      productId: values.productId ?? (currentQuote?.productId ? String(currentQuote.productId) : ''),
      systemSizeKw: parseFloat(values.systemSizeKw),
      systemFirstYearProductionKwh: parseFloat(values.systemFirstYearProductionKwh),
      kwhRate: values.kwhRate,
      escalationRate: values.selectedEscalationRate,
      reasons: values.reasons.map((r) => r.value),
      confirmedByEpc: values.confirmedByEpc,
    };

    // Battery is full-state: send the battery editor's current value (prepopulated from the contract,
    // editable in the UI). Omitting it removes the battery, so an untouched form resubmits it verbatim.
    // Map the editor's { label, value } selects back to the API's plain strings, with cost nested
    // inside battery (matching v3 create-quote; the create route splits it back out).
    if (values.battery?.type) {
      body.battery = {
        type: values.battery.type,
        pricingMethod: values.battery.pricingMethod,
        manufacturer: values.battery.manufacturer?.value,
        models: values.battery.models?.map((model) => ({
          modelNumber: model.modelNumber.value,
          quantity: model.quantity.value,
        })),
        ...(values.battery.cost !== undefined && { cost: values.battery.cost }),
      };
    }

    // The complete adder set: the toggled non-battery adders plus the selected battery's adder (the
    // backend requires a battery adder type whenever a battery is present, to drive the storage ITC
    // bracket; determinePricePerWatt only checks that one is present, not which variant).
    const addersArr: string[] = [];
    if (values.electricalUpgradeIncluded) addersArr.push(AdderType.electricalUpgrade);
    if (values.solarReadinessIncluded) addersArr.push(AdderType.solarReadiness);
    if (values.battery?.type) {
      addersArr.push(
        values.battery.type === BatteryType.arbitrageBattery ? AdderType.arbitrageBattery : AdderType.backupBattery,
      );
    }
    body.adders = addersArr;

    try {
      await createChangeOrder({ accountId: id!, body }).unwrap();
      toast.success('Change order created successfully');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      toast.error(e?.data?.message ?? 'Error creating change order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = useCallback(() => {
    navigate(`/accounts/${id}`);
  }, [navigate, id]);

  return (
    <>
      <Helmet>
        <title>Create Change Order</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          eyebrow={
            <Link to={`/accounts/${id}`} className="display-flex align-items-center flex-direction-row">
              <Icon name="caret-left" size="xs" color="primary" />
              <span className="font-color-primary m-left-2xs">{customerName}</span>
            </Link>
          }
          title="Create Change Order"
          description={`This addendum will amend the contract for ${
            customerName ?? 'this customer'
          }. The original contract will not be voided.`}
        />
        {isLoading ? (
          <Box padding="2xl" alignItems="center">
            <Spinner size="lg" />
          </Box>
        ) : !hasEligibleContract ? (
          <Box childGap="md">
            <Alert
              hasIcon
              variant="warning"
              message="A signed or approved contract is required to create a change order."
            />
            <Box fontWeight="bold" margin="0 0 sm 0">
              <Link to={`/accounts/${id}`}>
                <Icon name="caret-left" size="xs" color="primary" />
                <span className="font-color-primary m-left-2xs">Back to Account</span>
              </Link>
            </Box>
          </Box>
        ) : (
          <Box>
            <OriginalContractReferenceSection
              contractStatusLabel={contractStatusLabel}
              isApproved={currentContract?.status === ContractStatus.approved}
              productName={currentQuote?.productName}
              recipient={recipients[0]}
              contractPdfUrl={contractPdfUrl}
            />

            {/* Proposed values form. No enableReinitialize: the form only mounts once the loading
                guard clears (account/contract/quotes resolved), so these initialValues already
                reflect currentQuote at mount. Omitting it avoids a background refetch resetting
                fields the user is editing. */}
            <Formik<CreateChangeOrderFormValues>
              initialValues={{
                // Full-state submit: seed size/production from the current contract so an untouched
                // form resubmits the current values rather than sending NaN/blank. The CTA-supplied
                // as-built size (initialSystemSizeKw) still wins when present.
                systemSizeKw:
                  initialSystemSizeKw ||
                  (currentQuote?.systemSizeKw != null ? String(currentQuote.systemSizeKw) : ''),
                systemFirstYearProductionKwh:
                  initialProductionKwh ||
                  (currentQuote?.systemFirstYearProductionKwh != null
                    ? String(currentQuote.systemFirstYearProductionKwh)
                    : ''),
                reasons: undefined,
                confirmedByEpc: false,
                pricingOptionsLimitedByMonthlyPaymentCap: false,
                noPricingOptionsAvailable: false,
                // Reflect the current contract's adders so the form shows current state. Combined
                // with the submit always sending the adders array, an untouched submit preserves
                // them and unchecking one removes it.
                electricalUpgradeIncluded: currentQuote?.adders?.includes(AdderType.electricalUpgrade) ?? false,
                solarReadinessIncluded: currentQuote?.adders?.includes(AdderType.solarReadiness) ?? false,
                solarReadinessCost: currentQuote?.additionalCostAdders?.find(
                  (a) => a.type === AdditionalCostAdderType.solarReadiness,
                )?.cost,
                // Prepopulate the battery editor from the current contract; isBatterySelected drives
                // the battery validation so the seeded battery is treated as a complete selection.
                battery: initialBattery,
                isBatterySelected: !!initialBattery,
              }}
              onSubmit={handleSubmit}
              validationSchema={createChangeOrderSchema}
              validateOnChange={false}
            >
              {({ isSubmitting: _isSubmitting, errors, values }) => (
                <Form id="createChangeOrderForm">
                  <EscalationRateDefaulter />
                  <SelectedProductPricingSync />
                  <SolarEnergySystemSection
                    currentContractRows={[
                      {
                        value: currentQuote?.systemSizeKw != null ? `${currentQuote.systemSizeKw} kW` : '—',
                        label: 'System Size',
                      },
                      {
                        value:
                          currentQuote?.systemFirstYearProductionKwh != null
                            ? `${currentQuote.systemFirstYearProductionKwh.toLocaleString()} kWh`
                            : '—',
                        label: 'Year-1 Est. Production',
                      },
                    ]}
                  />
                  <AnnualEscalationRateSection
                    currentEscalationLabel={
                      currentQuote?.escalationRate != null
                        ? percentageFormatter.format(currentQuote.escalationRate)
                        : '—'
                    }
                  />
                  {!values.noPricingOptionsAvailable && (
                    <Adders
                      readOnlySolarReadinessCost
                      sideContent={
                        <CurrentContractCard
                          rows={currentAdderRows}
                          emptyMessage="No adders on the current contract."
                        />
                      }
                    />
                  )}
                  <PricingTable headerContent={<CurrentContractPricingRow quote={currentQuote} />} />
                  <ReasonSection />
                  <CustomerAlertDeliverySection
                    applicantEmail={recipients[0]?.email}
                    coApplicantEmail={recipients[1]?.email}
                  />
                  <ConfirmationSection />
                  {Object.keys(errors).length > 0 && errors.confirmedByEpc && (
                    <Box childGap="md" padding={{ base: '0 lg', desktop: '0' }}>
                      <Alert variant="danger" message={`Error: ${errors.confirmedByEpc}`} />
                    </Box>
                  )}
                  <ChangeOrderFormActions isLoading={isLoading} onCancel={handleCancel} />
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </MainContainer>
    </>
  );
};
